import offset from 'offset';
import { Component, createRef } from 'react';

import ClickAwayListener from '@mui/material/ClickAwayListener';
import Input from '@mui/material/Input';
import InputAdornment from '@mui/material/InputAdornment';
import Popper from '@mui/material/Popper';

export default class ImageResizer extends Component {
  state = {
    width: this.props.width,
  };
  rootEl = createRef();
  imgRef = createRef();
  inputRef = createRef();

  componentDidMount() {
    window.addEventListener('keydown', this.handleKeyDown);
  }
  componentWillUnmount() {
    this.removeListeners();
    window.removeEventListener('keydown', this.handleKeyDown);
  }

  startResize = e => {
    e.preventDefault();
    window.addEventListener('mouseup', this.endResize);
    window.addEventListener('mousemove', this.handleResizeMove);
    this.maxWidth = this.rootEl.current.closest(this.props.maxWidthParentSelector).clientWidth;
  };

  removeListeners() {
    window.removeEventListener('mouseup', this.endResize);
    window.removeEventListener('mousemove', this.handleResizeMove);
  }

  endResize = () => {
    this.removeListeners();
    const { height, width } = this.state;
    this.handleResize(width, height);
  };

  handleResizeMove = e => {
    const imgOffset = offset(this.imgRef.current);
    const width = Math.min(e.clientX - imgOffset.left, this.maxWidth);
    const height = Math.min(e.clientY - imgOffset.top, this.maxWidth * this.imgAR);
    if (height > width * this.imgAR) {
      this.setState({ width: height / this.imgAR, height, scaleRatio: (height / this.origImageHeight).toFixed(2) });
    } else {
      this.setState({ width, height: width * this.imgAR, scaleRatio: (width / this.origImageWidth).toFixed(2) });
    }
  };

  handleImageLoad = e => {
    const imgEl = e.target;
    this.origImageHeight = imgEl.naturalHeight;
    this.origImageWidth = imgEl.naturalWidth;
    this.imgAR = this.origImageHeight / this.origImageWidth;
    this.setState({ scaleRatio: (imgEl.clientWidth / this.origImageWidth).toFixed(2) });
    // if no width is saved to editor (images loaded for the first time)
    if (!this.state.width) {
      const width = this.origImageWidth;
      const height = this.origImageHeight;
      this.setState({ width, height });
      this.handleResize(width, height);
    }
  };

  handleSizeInputChange = e => {
    const { maxWidthParentSelector } = this.props;
    const value = parseFloat(e.target.value) || 0.1;
    const width = Math.min(
      this.origImageWidth * value,
      this.rootEl.current.closest(maxWidthParentSelector).clientWidth,
    );
    this.setState({
      width,
      height: width * this.imgAR,
      scaleRatio: e.target.value,
    });
    this.handleResize(width, width * this.imgAR);
  };

  handleResize(width, height) {
    this.props.onResizeComplete(Math.floor(width), Math.floor(height));
  }

  handleKeyDown = e => {
    if (e.key === 'Backspace' && e.target.type !== 'number') {
      this.props.onDelete?.();
    }
  };

  handleClickaway = e => {
    if (e.target !== this.inputRef.current) {
      this.props.onUnselectImage(e);
    }
  };

  render() {
    const { url } = this.props;
    const { scaleRatio, width } = this.state;
    return (
      <ClickAwayListener mouseEvent="onMouseDown" onClickAway={this.handleClickaway} touchEvent="onTouchEnd">
        <div css={rootCss} ref={this.rootEl}>
          <div className="IR-wrapper">
            <img
              alt="Placeholder"
              className="IR-img"
              height={this.props.height}
              onLoad={this.handleImageLoad}
              ref={this.imgRef}
              src={url}
              style={{ width }}
              width={this.props.width}
            />

            {this.imgRef.current && (
              <Popper anchorEl={this.imgRef.current} css={popperCss} open>
                <Input
                  className="IR-sizingInput"
                  disableUnderline
                  inputProps={{ step: '0.01', ref: this.inputRef }}
                  onChange={this.handleSizeInputChange}
                  startAdornment={<InputAdornment position="start">x</InputAdornment>}
                  type="number"
                  value={scaleRatio || ''}
                />
              </Popper>
            )}
            <div className="IR-dragHandle" onMouseDown={this.startResize} />
          </div>
        </div>
      </ClickAwayListener>
    );
  }
}

const rootCss = ({ palette }) => ({
  fontSize: 0, // remove extra space from inline-block wrapper
  '.IR-wrapper': {
    position: 'relative',
    display: 'inline-block',
    border: `2px solid ${palette.primary.light}`,
    margin: -2,
  },
  '.IR-img': {
    display: 'block',
    userSelect: 'none',
    maxWidth: '100%',
    height: 'auto',
    ...(palette.mode === 'dark' && { filter: 'invert(100%) hue-rotate(180deg)' }),
  },
  '.IR-dragHandle': {
    position: 'absolute',
    width: 10,
    height: 10,
    right: -5,
    bottom: -5,
    borderRadius: '50%',
    backgroundColor: palette.primary.main,
    border: `1px solid ${palette.primary.dark}`,
    cursor: 'nwse-resize',
    zIndex: 1, // easier to click/grab, not sure why
  },
});

const popperCss = ({ palette, shadows, shape: { borderRadius }, spacing, zIndex }) => ({
  zIndex: zIndex.modal + 2, // over the editor toolbar
  '.IR-sizingInput': {
    // position: 'absolute',
    width: 100,
    border: `2px solid ${palette.primary.light}`,
    // left: -2,
    // bottom: '100%',
    padding: spacing(0, 1),
    backgroundColor: palette.background.paper,
    boxShadow: shadows[2],
    borderRadius,
  },
});
